import { useCallback, useMemo, useRef, useState } from 'react';
import { formOptions, useStore } from '@tanstack/react-form';
import {
  Accordion,
  Alert,
  Button,
  Combobox,
  Field,
  Spinner,
  Tag,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { ArrowLeft, ArrowRight, Plus } from 'lucide-react';
import { useQuoteForm } from '@/features/quotes/hooks/common/use-quote-form.hook';
import { useRdIndemnityPeriods } from '@/features/quotes/hooks/rd-equipamentos/use-rd-indemnity-periods.hook';
import { useAddRdEquipment } from '@/features/quotes/hooks/rd-equipamentos/use-add-rd-equipment.hook';
import { useRdAgeBands } from '@/features/quotes/hooks/rd-equipamentos/use-rd-age-bands.hook';
import { useRdCommercialRule } from '@/features/quotes/hooks/rd-equipamentos/use-rd-commercial-rule.hook';
import { useRdCoverages } from '@/features/quotes/hooks/rd-equipamentos/use-rd-coverages.hook';
import { useRdEquipmentSearch } from '@/features/quotes/hooks/rd-equipamentos/use-rd-equipment-search.hook';
import { useRdModalities } from '@/features/quotes/hooks/rd-equipamentos/use-rd-modalities.hook';
import { useRdProposal } from '@/features/quotes/hooks/rd-equipamentos/use-rd-proposal.hook';
import { useRdRateGrid } from '@/features/quotes/hooks/rd-equipamentos/use-rd-rate-grid.hook';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { lookupCep } from '@/features/quotes/utils/rd-cep-lookup.util';
import { useFetchZipcode } from '@/shared/hooks/use-fetch-zipcode.hook';
import type { StepEquipamentoFormValues } from '@/features/quotes/schemas/rd-equipamentos/step-equipamento.schema';
import type { RdQuoteStepProps } from '@/features/quotes/components/rd-equipamentos/rd-quote-step.types';
import type {
  RdAgeBand,
  RdCatalogEquipment,
} from '@/services/quotes/rd-equipamentos/types';
import {
  brlInputToCents,
  centsToBRL,
  MICRO,
} from '@/features/quotes/utils/rd-units.util';
import { formatCurrencyInputValue } from '@/shared/utils/currencyFormatter';
import { useDebouncedValue } from '@/shared/utils/use-filters.hook';
import { MODALITY_LABELS } from '@/features/quotes/utils/rd-modality.util';
import { EquipmentCard } from './sections/equipment-card.component';
import type { CoverageSaveStatus } from './sections/coverage-matrix.component';
import { CoverageRowsTable } from './sections/coverage-rows-table.component';
import {
  applyLmiChange,
  applyToggle,
  buildRowsForModality,
  recomputeLimits,
  isBasicaInvalid,
  isOverLimit,
  applyIndemnityChange,
  missingIndemnitySlugs,
  type CoverageRow,
} from './sections/coverage-rows.util';
import { pickError } from './utils/step-helpers.util';
import { StepActions } from './sections/step-actions.component';

const CURRENT_YEAR = new Date().getFullYear();

/**
 * `formatCurrencyInputValue` trunca em R$ 9.999.999,99 por padrão
 * (`CURRENCY_INPUT_MAX_DIGITS = 9`) — máquinas pesadas podem valer ≥ R$ 10M,
 * então o campo de valor do equipamento passa um `maxDigits` maior
 * explicitamente (mesmo padrão do step de Frota).
 */
const EQUIPMENT_VALUE_MAX_DIGITS = 12;

/**
 * Erro do campo Valor — teto de `modality.lmiMax`. Como a Básica é derivada
 * do valor do equipamento (o motor força `lmi = equipment.value` em
 * `Proposal.set_coverages`), um item acima do teto da modalidade é
 * insegurável, e o motor o recusa em `add_equipment` com `FormInvalid` no
 * campo `value`. Pré-validar aqui evita o round-trip.
 *
 * Função pura, no padrão de `getCommissionError` em
 * `step-parametros.component.tsx`: chamada no render e na guarda de submit,
 * para o texto nunca ficar preso a um valor antigo.
 *
 * Vazio/zero devolve `undefined` de propósito — o piso é do Zod
 * (`StepEquipamentoSchema`, `value: z.number().int().min(1)`), e duplicar a
 * mensagem aqui faria o erro aparecer antes de o usuário terminar de digitar.
 */
function getEquipmentValueError(
  value: string,
  lmiMax: number | undefined
): string | undefined {
  if (lmiMax === undefined) return undefined;
  const cents = brlInputToCents(value);
  if (cents <= 0) return undefined;
  return cents > lmiMax
    ? `Valor acima do teto da modalidade (${centsToBRL(lmiMax)}).`
    : undefined;
}

/**
 * Erro do campo Ano — teto de idade do produto
 * (`CommercialRule.accept_max_age_years`, parametrizável em Admin → Regras).
 * O motor recusa o cadastro (`Proposal.add_equipment`); checar aqui evita o
 * round-trip e diz o ano mínimo, que é a informação acionável.
 *
 * Função pura, no mesmo padrão de `getEquipmentValueError`: chamada no render
 * e na guarda de submit, para o texto nunca ficar preso a um valor antigo.
 * Ano vazio devolve `undefined` — quem cobra o preenchimento é o Zod.
 */
function getEquipmentYearError(
  year: string,
  maxAgeYears: number | undefined
): string | undefined {
  if (maxAgeYears === undefined) return undefined;
  const parsed = Number(year);
  if (!year || !Number.isFinite(parsed)) return undefined;
  const idade = CURRENT_YEAR - parsed;
  return idade > maxAgeYears
    ? `Equipamento com ${idade} anos: acima do limite de ${maxAgeYears} anos aceito para seguro.`
    : undefined;
}

/**
 * Resolve a faixa de agravação de um ano, com a MESMA regra do motor
 * (`modules/pricing_core.age_factor`): percorre na ordem recebida e a primeira
 * que casa vence; `minYear`/`maxYear` nulos deixam a faixa aberta daquele
 * lado. Preservar a ordem do array é o que impede a tela de anunciar um agravo
 * diferente do que será cobrado.
 */
function findAgeBand(
  year: string,
  bands: RdAgeBand[] | undefined
): RdAgeBand | undefined {
  const parsed = Number(year);
  if (!year || !Number.isFinite(parsed) || !bands) return undefined;
  return bands.find(
    band =>
      (band.minYear === null || parsed >= band.minYear) &&
      (band.maxYear === null || parsed <= band.maxYear)
  );
}

/**
 * Espelha a validação do backend (`Proposal.add_equipment`): a modalidade com
 * `requires_location` (Estacionário) exige o CEP do risco DO ITEM — 422 com
 * `errors.risk_cep`. Checar client-side evita o round-trip; o fallback do erro
 * de backend continua ativo via `submitError` (ver `useAddRdEquipment`).
 */
const RISK_LOCATION_REQUIRED_MESSAGE =
  'Esta modalidade exige o CEP do local de risco do equipamento.';

const formOpts = formOptions({
  defaultValues: {
    equipmentCode: '',
    year: '',
    value: '',
    brand: '',
    model: '',
    serialNumber: '',
    isZeroKm: false,
    nfNumber: '',
    nfDate: '',
    nfIssuer: '',
    riskCep: '',
    riskAddress: '',
    riskNumber: '',
    riskComplement: '',
    riskDistrict: '',
    riskCity: '',
    riskState: '',
  } as StepEquipamentoFormValues,
});

type EquipmentOption = {
  label: string;
  value: string;
  modalitySlug: RdCatalogEquipment['modalitySlug'];
};

/**
 * `Combobox.Root.onInputValueChange` do design system só declara
 * `{ inputValue: string }`, mas o zag-js `@zag-js/combobox` por baixo
 * sempre inclui `reason` (`InputValueChangeReason`) e o wrapper repassa o
 * objeto completo — ver uso em `onInputValueChange` abaixo.
 */
type ComboboxInputValueChangeDetails = {
  inputValue: string;
  reason?:
    | 'input-change'
    | 'item-select'
    | 'clear-trigger'
    | 'script'
    | 'interact-outside';
};

/**
 * Step 2 — Equipamentos (RD Equipamentos, fluxo individual): lista os itens
 * já adicionados (`EquipmentCard`, cada um expansível na SUA matriz de
 * coberturas) + um formulário de adicionar sempre visível (sem limite de
 * itens, ver brief da Task 4) que também coleta as coberturas do item que
 * está sendo cadastrado.
 *
 * As coberturas viviam num Step 3 separado, que listava os mesmos itens de
 * novo. Aqui elas ficam onde o equipamento é definido: as disponíveis saem de
 * `rateGrid[modalitySlug]`, e a modalidade já é conhecida no instante da
 * seleção no combobox (`RdCatalogEquipment.modalitySlug`).
 *
 * `onNext` exige `proposal.equipment.length >= 1` E nenhuma matriz pendente
 * ou inválida (`hasPendingOrError`) — o autosave por item é debounced, então
 * sem esse gate uma edição recém-digitada se perderia ao navegar.
 */
export function StepEquipamentos({
  onNext,
  onBack,
  canGoBack = true,
  isViewMode = false,
}: RdQuoteStepProps) {
  const form = useQuoteForm({ ...formOpts });
  const { setFieldValue } = form;

  const { id, policyId } = useQuoteParams();
  const resolvedProposalId = (id || policyId || '').trim();
  const { data: proposal, isLoading: isProposalLoading } = useRdProposal(
    resolvedProposalId || undefined
  );
  const { data: modalities } = useRdModalities();
  const { data: commercialRule } = useRdCommercialRule();
  const { data: ageBands } = useRdAgeBands();
  const { data: rateGrid, isLoading: isRateGridLoading } = useRdRateGrid();
  const { data: coverages, isLoading: isCoveragesLoading } = useRdCoverages();
  // Prazos do cadastro: o rádio da matriz os desenha na ordem do motor.
  const { data: indemnityPeriods } = useRdIndemnityPeriods();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 200);
  const { data: equipmentResults = [] } = useRdEquipmentSearch(debouncedSearch);

  const [selectedEquipment, setSelectedEquipment] =
    useState<RdCatalogEquipment | null>(null);
  // Mirrors `selectedEquipment` but updates synchronously (refs aren't
  // batched like state). The zag-js combobox machine fires
  // `onValueChange` then `onInputValueChange` back-to-back in the same
  // flush when an item is selected, so `onInputValueChange` would
  // otherwise close over the *previous* render's `selectedEquipment` and
  // wrongly clear a fresh selection. Reading the ref instead guarantees
  // `onInputValueChange` sees the just-picked item.
  //
  // NOTE: the design system's `Combobox.Root` hardcodes
  // `selectionBehavior="clear"`, so zag ALSO fires `onInputValueChange`
  // with `inputValue: ''` right after every selection (not just when the
  // user types) — `reason` on that event is `'item-select'`, not
  // `'input-change'`. The handler below only treats the change as "user
  // typed something new" when `reason === 'input-change'`; otherwise the
  // post-selection clear would immediately wipe out the pick just made.
  const selectedEquipmentRef = useRef<RdCatalogEquipment | null>(null);

  const selectedModality = modalities?.find(
    m => m.slug === selectedEquipment?.modalitySlug
  );

  const { mutateAsync: fetchRiskZipcode, isPending: isFetchingRiskCep } =
    useFetchZipcode('quotation');
  const riskNumberContainerRef = useRef<HTMLDivElement>(null);

  /**
   * Último item já cadastrado que tenha endereço — fonte do botão "mesmo
   * endereço do item anterior". Com o endereço por item, três estacionários
   * no mesmo galpão obrigariam a redigitar tudo três vezes.
   */
  const previousItemWithAddress = [...(proposal?.equipment ?? [])]
    .reverse()
    .find(item => item.riskCep);

  function copyPreviousItemAddress() {
    if (!previousItemWithAddress) return;
    setFieldValue('riskCep', previousItemWithAddress.riskCep ?? '');
    setFieldValue('riskAddress', previousItemWithAddress.riskAddress ?? '');
    setFieldValue('riskNumber', previousItemWithAddress.riskNumber ?? '');
    setFieldValue(
      'riskComplement',
      previousItemWithAddress.riskComplement ?? ''
    );
    setFieldValue('riskDistrict', previousItemWithAddress.riskDistrict ?? '');
    setFieldValue('riskCity', previousItemWithAddress.riskCity ?? '');
    setFieldValue('riskState', previousItemWithAddress.riskState ?? '');
  }

  /**
   * Mensagem client-side de bloqueio (modalidade exige CEP/endereço do
   * risco e o item ainda não tem `riskCep`) — ver
   * `RISK_LOCATION_REQUIRED_MESSAGE` e `handleAddClick` abaixo. Distinto de
   * `submitError` (erro vindo do backend após o submit real).
   */
  const [locationError, setLocationError] = useState<string | null>(null);

  const equipmentOptions: EquipmentOption[] = useMemo(
    () =>
      equipmentResults.map(equipment => ({
        label: equipment.name,
        value: equipment.code,
        modalitySlug: equipment.modalitySlug,
      })),
    [equipmentResults]
  );

  function clearZeroKmFields() {
    setFieldValue('isZeroKm', false);
    setFieldValue('nfNumber', '');
    setFieldValue('nfDate', '');
    setFieldValue('nfIssuer', '');
  }

  function handleClearSelection() {
    selectedEquipmentRef.current = null;
    setSelectedEquipment(null);
    setFieldValue('equipmentCode', '');
    clearZeroKmFields();
    setLocationError(null);
  }

  /**
   * Coberturas do item que está sendo cadastrado. Ficam FORA do TanStack
   * Form: carregam metadados derivados de query (`name`, `mandatory`, ordem)
   * e o `StepEquipamentoSchema` não descreve coberturas. Guardadas junto com
   * o código do equipamento a que pertencem e DERIVADAS no render — assim um
   * refetch em background de `rateGrid`/`coverages` (staleTime 5min) não
   * apaga o que o corretor acabou de digitar, como um `useEffect` de
   * sincronização faria ao ver uma nova referência de array.
   */
  const [newItemRows, setNewItemRows] = useState<{
    equipmentCode: string;
    rows: CoverageRow[];
  } | null>(null);

  const { handleSubmit, isPending, submitAttempted, submitError } =
    useAddRdEquipment({
      proposalId: resolvedProposalId,
      form,
      onAdded: () => {
        setSearch('');
        selectedEquipmentRef.current = null;
        setSelectedEquipment(null);
        setLocationError(null);
        // `form.reset()` não alcança este estado — sem isto as coberturas do
        // item anterior vazariam para o próximo.
        setNewItemRows(null);
      },
    });

  const isZeroKmValue = useStore(form.store, state => state.values.isZeroKm);
  const yearValue = useStore(form.store, state => state.values.year);

  const maxAgeYears = commercialRule?.acceptMaxAgeYears;
  const minYearAceito =
    maxAgeYears === undefined ? undefined : CURRENT_YEAR - maxAgeYears;
  /**
   * Faixa de agravação do ano digitado, na modalidade do equipamento
   * escolhido. Só vira aviso quando a modalidade de fato PRECIFICA idade — o
   * motor consulta o fator nas três, mas neutraliza em Estacionário e Portátil
   * (`uses_age`), e anunciar um agravo que não entra no prêmio seria mentira.
   */
  const ageBand = findAgeBand(
    yearValue,
    selectedEquipment ? ageBands?.[selectedEquipment.modalitySlug] : undefined
  );
  const agravoPercent =
    selectedModality?.usesAge && ageBand && ageBand.factor > MICRO
      ? Math.round(((ageBand.factor - MICRO) / MICRO) * 100)
      : undefined;
  const valueInput = useStore(form.store, state => state.values.value);

  const equipmentList = proposal?.equipment ?? [];
  const equipmentCount = equipmentList.length;

  const [itemStatuses, setItemStatuses] = useState<
    Record<string, CoverageSaveStatus>
  >({});

  const handleStatusChange = useCallback(
    (itemId: string, status: CoverageSaveStatus) => {
      setItemStatuses(prev =>
        prev[itemId] === status ? prev : { ...prev, [itemId]: status }
      );
    },
    []
  );

  /**
   * `hasPendingOrError` cobre as matrizes dos itens JÁ adicionados.
   * `CoverageMatrix` reporta `'saving'` de forma SÍNCRONA no instante da
   * edição (antes do debounce de 500ms), o que fecha a janela em que
   * "Próximo" era clicado antes do debounce disparar, desmontando a matriz e
   * cancelando o save pendente. Não remover `'saving'` deste `.some(...)`.
   * `'invalid'` é a Básica com LMI 0/vazio — distinto de `'error'`, que
   * significa "o PUT falhou".
   */
  const hasPendingOrError = Object.values(itemStatuses).some(
    itemStatus =>
      itemStatus === 'saving' ||
      itemStatus === 'error' ||
      itemStatus === 'invalid'
  );

  /**
   * Linhas exibidas no formulário de adicionar. A linha `mandatory` (Básica)
   * é readonly e espelha o campo Valor em tempo real — o motor deriva
   * `lmi = equipment.value` em `set_coverages` e descarta o que for enviado,
   * então mostramos a mesma fonte.
   */
  const addFormRows: CoverageRow[] = useMemo(() => {
    if (!selectedEquipment || !rateGrid || !coverages) return [];
    const base =
      newItemRows?.equipmentCode === selectedEquipment.code
        ? newItemRows.rows
        : buildRowsForModality(
            selectedEquipment.modalitySlug,
            0,
            rateGrid,
            coverages
          );
    // Recalcula contra o Valor digitado: os tetos por cobertura e a linha
    // derivada (Despesa de Salvamento) dependem da Básica, que aqui é o
    // campo Valor — e ele é preenchido DEPOIS de a matriz existir.
    return recomputeLimits(base, brlInputToCents(valueInput));
  }, [selectedEquipment, rateGrid, coverages, newItemRows, valueInput]);

  function updateAddFormRows(next: CoverageRow[]) {
    if (!selectedEquipment) return;
    setNewItemRows({ equipmentCode: selectedEquipment.code, rows: next });
  }

  const blockInteraction =
    isPending || isProposalLoading || isRateGridLoading || isCoveragesLoading;
  const disabled = isViewMode || blockInteraction;
  const showZeroKm = selectedEquipment?.modalitySlug === 'movel';

  function handleNext() {
    if (equipmentCount < 1 || hasPendingOrError) return;
    onNext();
  }

  /**
   * Pré-empta o 422 de `add_equipment` para os dois erros que o motor
   * devolve por campo. O de endereço deixou de precisar de tratamento
   * especial: o CEP do risco agora vive NESTE formulário, então é validação
   * de campo comum — antes ele morava no Step Cliente e a checagem tinha de
   * olhar `proposal.riskCep`.
   */
  function handleAddClick() {
    if (
      selectedModality?.requiresLocation &&
      !form.state.values.riskCep?.trim()
    ) {
      setLocationError(RISK_LOCATION_REQUIRED_MESSAGE);
      return;
    }
    setLocationError(null);
    // Pré-empta o 422 de `add_equipment` ("Valor do equipamento fora da
    // faixa aceita para esta modalidade."), que o motor devolve no campo
    // `value`. Sem isto o usuário paga um round-trip por um erro conhecido.
    if (
      getEquipmentValueError(form.state.values.value, selectedModality?.lmiMax)
    ) {
      return;
    }
    // Mesmo motivo: o motor recusa o cadastro acima do teto de idade
    // (`Proposal.add_equipment`), e o erro já está na tela.
    if (getEquipmentYearError(form.state.values.year, maxAgeYears)) {
      return;
    }
    // Guarda defensiva: a Básica espelha o campo Valor, que o Zod já exige
    // `>= 1` — mas sem ela um valor zerado produziria um item sem cobertura
    // obrigatória, que o motor recusa.
    if (isBasicaInvalid(addFormRows)) {
      return;
    }
    // Mesmo contrato do card de item já salvo (`coverage-matrix`): linha acima
    // do teto da cobertura não vai ao motor. Aqui a recusa seria um 400 por
    // slug depois de o item já ter sido criado — a mensagem já está na linha,
    // basta não enviar.
    if (isOverLimit(addFormRows)) {
      return;
    }
    // Mesmo contrato dos demais gates: o motor barra a COTAÇÃO sem período,
    // e deixar o item nascer sem ele empurraria o erro para dois passos
    // adiante, longe de onde se escolhe.
    if (missingIndemnitySlugs(addFormRows).length > 0) {
      return;
    }
    handleSubmit({
      coverageRows: addFormRows,
      existingItemIds: equipmentList.map(item => item.itemId),
    });
  }

  return (
    <div className='flex flex-col gap-8'>
      <div className='flex flex-col gap-4'>
        {equipmentList.length === 0 ? (
          <div className='rounded-xl border border-dashed border-alba-gray-400 bg-alba-gray-100 px-4 py-6 text-center'>
            <Text className='text-sm text-alba-gray-500'>
              Nenhum equipamento adicionado ainda.
            </Text>
          </div>
        ) : rateGrid && coverages ? (
          <Accordion.Root
            multiple
            collapsible
            defaultValue={[
              equipmentList[equipmentList.length - 1]?.itemId ?? '',
            ]}
            className='flex flex-col gap-4'
          >
            {equipmentList.map(item => (
              <EquipmentCard
                key={item.itemId}
                item={item}
                proposalId={resolvedProposalId}
                rateGrid={rateGrid}
                coverages={coverages}
                status={itemStatuses[item.itemId]}
                disabled={isViewMode}
                onStatusChange={handleStatusChange}
              />
            ))}
          </Accordion.Root>
        ) : null}
      </div>

      {!isViewMode && (
        <div className='flex flex-col gap-6 rounded-2xl border border-alba-gray-200/80 p-6'>
          <Text className='text-base text-alba-black-500'>
            Adicionar equipamento
          </Text>

          <form.AppField
            name='equipmentCode'
            children={field => {
              const errorMessage = pickError(field.state.meta, submitAttempted);
              return (
                <Field.Root required invalid={!!errorMessage}>
                  <Field.Label>Equipamento *</Field.Label>
                  <Combobox.Root
                    items={equipmentOptions}
                    value={field.state.value ? [field.state.value] : []}
                    onValueChange={details => {
                      const code = details.value[0] || '';
                      field.handleChange(code);
                      const picked =
                        equipmentResults.find(
                          equipment => equipment.code === code
                        ) ?? null;
                      // Update the ref synchronously, before
                      // `onInputValueChange` runs, so its stale-selection
                      // guard below always sees the item just picked here
                      // rather than the previous render's selection.
                      selectedEquipmentRef.current = picked;
                      setSelectedEquipment(picked);
                      setLocationError(null);
                      if (!picked || picked.modalitySlug !== 'movel') {
                        clearZeroKmFields();
                      }
                    }}
                    onInputValueChange={(
                      details: ComboboxInputValueChangeDetails
                    ) => {
                      setSearch(details.inputValue);
                      // Ignora o clear-de-input que o próprio zag dispara
                      // logo após uma seleção (`selectionBehavior="clear"`
                      // — ver nota em `selectedEquipmentRef` acima). Só o
                      // `reason === 'input-change'` (digitação real do
                      // usuário) deve poder invalidar a seleção atual.
                      if (details.reason && details.reason !== 'input-change') {
                        return;
                      }
                      const currentSelection = selectedEquipmentRef.current;
                      if (
                        currentSelection &&
                        details.inputValue !== currentSelection.name
                      ) {
                        handleClearSelection();
                      }
                    }}
                    disabled={disabled}
                  >
                    <Combobox.Control>
                      <Combobox.Trigger>
                        {selectedEquipment?.name || 'Selecione o equipamento'}
                      </Combobox.Trigger>
                    </Combobox.Control>
                    <Combobox.Portal>
                      <Combobox.Positioner>
                        <Combobox.Content>
                          <Combobox.Input
                            placeholder='Digite para buscar...'
                            autoFocus
                          />
                          <Combobox.List>
                            {equipmentOptions.length === 0 &&
                            debouncedSearch.trim().length >= 2 ? (
                              <Combobox.Empty>
                                Nenhum equipamento encontrado.
                              </Combobox.Empty>
                            ) : (
                              <Combobox.ItemGroup>
                                <Combobox.ItemGroupLabel>
                                  Equipamentos
                                </Combobox.ItemGroupLabel>
                                {equipmentOptions.map(option => (
                                  <Combobox.Item
                                    key={option.value}
                                    item={option}
                                  >
                                    <Combobox.ItemText>
                                      {option.label}
                                    </Combobox.ItemText>
                                    <Combobox.ItemIndicator />
                                  </Combobox.Item>
                                ))}
                              </Combobox.ItemGroup>
                            )}
                          </Combobox.List>
                        </Combobox.Content>
                      </Combobox.Positioner>
                    </Combobox.Portal>
                  </Combobox.Root>
                  {selectedEquipment && (
                    <div className='flex items-center gap-2 pt-1'>
                      <Tag.Root size='sm' colorScheme='blue'>
                        <Tag.Label>
                          {MODALITY_LABELS[selectedEquipment.modalitySlug]}
                        </Tag.Label>
                      </Tag.Root>
                    </div>
                  )}
                  {errorMessage && (
                    <Field.ErrorText>{errorMessage}</Field.ErrorText>
                  )}
                </Field.Root>
              );
            }}
          />

          <div className='grid grid-cols-1 gap-6 lg:grid-cols-3'>
            <form.AppField
              name='year'
              children={field => {
                // `Field.Root` custom em vez do `NumberField`: o teto de idade
                // vem de configuração e precisa virar erro do campo, e o
                // `NumberField` não aceita erro de fora — mesmo motivo pelo
                // qual o campo Valor já foi escrito assim.
                const ceilingError = getEquipmentYearError(
                  field.state.value,
                  maxAgeYears
                );
                const errorMessage =
                  ceilingError ?? pickError(field.state.meta, submitAttempted);
                return (
                  <Field.Root required invalid={!!errorMessage}>
                    <Field.Label>
                      Ano <span className='text-alba-error-red-500'>*</span>
                    </Field.Label>
                    <Field.Input
                      type='number'
                      inputMode='numeric'
                      value={field.state.value}
                      onChange={e => field.handleChange(e.target.value)}
                      min={minYearAceito}
                      max={CURRENT_YEAR}
                      disabled={disabled}
                    />
                    {errorMessage ? (
                      <Field.ErrorText>{errorMessage}</Field.ErrorText>
                    ) : agravoPercent !== undefined ? (
                      <Field.HelperText>
                        Agravo por idade: +{agravoPercent}% (equipamento com{' '}
                        {CURRENT_YEAR - Number(field.state.value)} anos).
                      </Field.HelperText>
                    ) : minYearAceito !== undefined ? (
                      <Field.HelperText>
                        a partir de {minYearAceito}
                      </Field.HelperText>
                    ) : null}
                  </Field.Root>
                );
              }}
            />
            <form.AppField
              name='value'
              children={field => {
                const ceilingError = getEquipmentValueError(
                  field.state.value,
                  selectedModality?.lmiMax
                );
                const errorMessage =
                  ceilingError ?? pickError(field.state.meta, submitAttempted);
                return (
                  <Field.Root invalid={!!errorMessage}>
                    <Field.Label>
                      Valor <span className='text-alba-error-red-500'>*</span>
                    </Field.Label>
                    <Field.Input
                      type='text'
                      inputMode='decimal'
                      value={field.state.value}
                      onChange={e =>
                        field.handleChange(
                          formatCurrencyInputValue(
                            e.target.value,
                            EQUIPMENT_VALUE_MAX_DIGITS
                          )
                        )
                      }
                      placeholder='R$ 0,00'
                      disabled={disabled}
                    />
                    {errorMessage ? (
                      <Field.ErrorText>{errorMessage}</Field.ErrorText>
                    ) : selectedModality ? (
                      <Field.HelperText>
                        máximo {centsToBRL(selectedModality.lmiMax)}
                      </Field.HelperText>
                    ) : null}
                  </Field.Root>
                );
              }}
            />
          </div>

          <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
            <form.AppField
              name='brand'
              children={field => (
                <field.TextField label='Marca' required disabled={disabled} />
              )}
            />
            <form.AppField
              name='model'
              children={field => (
                <field.TextField
                  label='Modelo'
                  required
                  maxLength={96}
                  disabled={disabled}
                />
              )}
            />
          </div>

          {/* Comprovação em caso de sinistro (não entra no cálculo). Exigido
              já no cadastro do equipamento (decisão PO): o motor cobra o campo
              em todos os itens ao efetivar a proposta. */}
          <form.AppField
            name='serialNumber'
            children={field => (
              <field.TextField
                label='Chassi / nº de série'
                required
                maxLength={96}
                helperText='Comprovação em caso de sinistro (não entra no cálculo).'
                disabled={disabled}
              />
            )}
          />

          {showZeroKm && (
            <div className='flex flex-col gap-4'>
              <form.AppField
                name='isZeroKm'
                children={field => (
                  <field.CheckboxField
                    label='Zero km (equipamento novo, requer nota fiscal)'
                    disabled={disabled}
                  />
                )}
              />

              {isZeroKmValue && (
                <div className='grid grid-cols-1 gap-6 lg:grid-cols-3'>
                  <form.AppField
                    name='nfNumber'
                    children={field => (
                      <field.TextField
                        label='Número da nota fiscal'
                        disabled={disabled}
                      />
                    )}
                  />
                  <form.AppField
                    name='nfDate'
                    children={field => (
                      <field.CalendarField
                        label='Data de saída da nota fiscal'
                        required
                        maxDate={new Date().toISOString().slice(0, 10)}
                        disabled={disabled}
                      />
                    )}
                  />
                  <form.AppField
                    name='nfIssuer'
                    children={field => (
                      <field.TextField
                        label='Emissor da nota fiscal'
                        disabled={disabled}
                      />
                    )}
                  />
                </div>
              )}
            </div>
          )}

          {/* Local de risco DESTE item. Só aparece quando a modalidade exige
              (`requires_location` — Estacionário); Móvel/Portátil são
              cobertos pelo âmbito de cobertura da proposta. */}
          {selectedModality?.requiresLocation ? (
            <div className='flex flex-col gap-6'>
              <div className='flex flex-wrap items-center justify-between gap-3'>
                <Text className='text-lg font-bold text-alba-black-500'>
                  Local de risco do equipamento
                </Text>
                {previousItemWithAddress ? (
                  <Button
                    variant='ghost'
                    size='sm'
                    onClick={copyPreviousItemAddress}
                    disabled={disabled}
                  >
                    Mesmo endereço do item anterior
                  </Button>
                ) : null}
              </div>
              <div className='grid grid-cols-1 gap-6 lg:grid-cols-3'>
                <form.AppField
                  name='riskCep'
                  listeners={{
                    onBlur: ({ value }) =>
                      lookupCep({
                        zipcode: value,
                        fetchFn: fetchRiskZipcode,
                        policyId: resolvedProposalId || undefined,
                        fields: {
                          address: 'riskAddress',
                          district: 'riskDistrict',
                          city: 'riskCity',
                          state: 'riskState',
                        },
                        setFieldValue,
                        onFilled: () =>
                          riskNumberContainerRef.current
                            ?.querySelector('input')
                            ?.focus(),
                      }),
                  }}
                  children={field => (
                    <field.TextField
                      label='CEP'
                      mask='cep'
                      required
                      placeholder='00000-000'
                      disabled={disabled || isFetchingRiskCep}
                    />
                  )}
                />
                <div ref={riskNumberContainerRef}>
                  <form.AppField
                    name='riskNumber'
                    children={field => (
                      <field.TextField label='Número' disabled={disabled} />
                    )}
                  />
                </div>
                <form.AppField
                  name='riskComplement'
                  children={field => (
                    <field.TextField label='Complemento' disabled={disabled} />
                  )}
                />
              </div>
              <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
                <form.AppField
                  name='riskAddress'
                  children={field => (
                    <field.TextField label='Endereço' disabled={disabled} />
                  )}
                />
                <form.AppField
                  name='riskDistrict'
                  children={field => (
                    <field.TextField label='Bairro' disabled={disabled} />
                  )}
                />
              </div>
              <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
                <form.AppField
                  name='riskCity'
                  children={field => (
                    <field.TextField label='Cidade' disabled={disabled} />
                  )}
                />
                <form.AppField
                  name='riskState'
                  children={field => (
                    <field.TextField
                      label='UF'
                      maxLength={2}
                      disabled={disabled}
                    />
                  )}
                />
              </div>
            </div>
          ) : null}

          {/* Coberturas DESTE equipamento. As linhas saem de
              `rateGrid[modalitySlug]` — ausência de um slug = a cobertura não
              existe naquela modalidade, e é isso que faz a matriz variar
              entre móvel, estacionário e portátil. Só aparece depois da
              seleção do equipamento, quando a modalidade é conhecida. */}
          {selectedEquipment && addFormRows.length > 0 ? (
            <div className='flex flex-col gap-4'>
              <Text className='text-lg font-bold text-alba-black-500'>
                Coberturas
              </Text>
              <CoverageRowsTable
                rows={addFormRows}
                disabled={disabled}
                onToggle={slug =>
                  updateAddFormRows(applyToggle(addFormRows, slug))
                }
                onLmiChange={(slug, raw) =>
                  updateAddFormRows(applyLmiChange(addFormRows, slug, raw))
                }
                indemnityPeriods={indemnityPeriods}
                onIndemnityChange={(slug, months) =>
                  updateAddFormRows(
                    applyIndemnityChange(addFormRows, slug, months)
                  )
                }
              />
              <Text className='text-xs text-alba-gray-500'>
                As coberturas são salvas junto com o equipamento e podem ser
                editadas depois no card do item.
              </Text>
            </div>
          ) : null}

          {locationError || submitError ? (
            <Alert.Root status='error'>
              <Alert.Icon />
              <Alert.Content>
                <Alert.Title>
                  {locationError
                    ? 'Local de risco necessário'
                    : 'Não foi possível adicionar o equipamento'}
                </Alert.Title>
                <Alert.Description>
                  {locationError || submitError}
                </Alert.Description>
              </Alert.Content>
            </Alert.Root>
          ) : null}

          <div className='flex justify-end'>
            <Button
              variant='secondary'
              size='md'
              className='flex items-center gap-2'
              onClick={handleAddClick}
              disabled={disabled}
            >
              {isPending ? (
                <>
                  Adicionando...
                  <Spinner size='sm' colorScheme='gray' />
                </>
              ) : (
                <>
                  <Plus className='h-5 w-5' strokeWidth={1.6} />
                  Adicionar equipamento
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      <StepActions>
        <Button
          variant='ghost'
          size='lg'
          className='flex items-center gap-2'
          onClick={onBack}
          disabled={isViewMode || !canGoBack || blockInteraction}
        >
          <ArrowLeft className='h-5 w-5' strokeWidth={1.6} />
          Voltar
        </Button>
        <Button
          variant='primary'
          size='md'
          className='flex items-center gap-2'
          onClick={handleNext}
          disabled={
            isViewMode ||
            equipmentCount < 1 ||
            blockInteraction ||
            hasPendingOrError
          }
        >
          Próximo
          <ArrowRight className='h-6 w-6' strokeWidth={2} />
        </Button>
      </StepActions>
    </div>
  );
}
